import type { BX24Result, CrmDeal, PlacementInfo } from './types';
// Disk operations moved to features/disk — re-exported below for backward compatibility

export function initBX24(): Promise<void> {
  return new Promise((resolve) => {
    window.BX24.init(resolve);
  });
}

export function getPlacementInfo(): Promise<PlacementInfo> {
  return Promise.resolve(window.BX24.placement.info());
}

function callMethod<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  return new Promise((resolve, reject) => {
    window.BX24.callMethod(method, params, (result: BX24Result<T>) => {
      const err = result.error();
      if (err) {
        reject(new Error(`BX24 ${method}: ${err.description} (${err.code})`));
      } else {
        resolve(result.data());
      }
    });
  });
}

export function getDeal(id: string): Promise<CrmDeal> {
  return callMethod<CrmDeal>('crm.deal.get', { id });
}

/** Fetch deal including custom UF_ fields. CrmDeal is typed as Record for UF fields. */
export function getDealRaw(id: string): Promise<Record<string, unknown>> {
  return callMethod<Record<string, unknown>>('crm.deal.get', { id });
}

/**
 * Post a timeline comment on the deal with attached files.
 * FILES format: [['filename.xlsx', 'base64string'], ...]
 * Returns the new comment ID.
 */
export function uploadTimelineComment(
  dealId: string,
  comment: string,
  files: Array<{ name: string; base64: string }>,
): Promise<number> {
  return callMethod<number>('crm.timeline.comment.add', {
    fields: {
      ENTITY_ID: parseInt(dealId, 10),
      ENTITY_TYPE: 'deal',
      COMMENT: comment,
      FILES: files.map((f) => [f.name, f.base64]),
    },
  });
}

// ─── Disk operations re-exported from features/disk for backward compatibility ──
export {
  getDiskFolderChildren,
  findSubfolderByPrefix,
  uploadFileToDiskFolder,
  uploadFileVersion,
  upsertFileToDiskFolder,
  getDiskFile,
  getAttachedObject,
  fetchDiskFile,
  fetchDiskFileById,
} from '../disk/bx24Disk';
